import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, CheckCircle2, Download, Eye, FileCheck2, Trash2 } from 'lucide-react'
import { Button, Chip, EmptyState, PageHeaderBand } from '@/components/ui'
import {
  letterPdfUrl,
  useAttachLetter,
  useDeleteLetter,
  useFinalizeLetter,
  usePatchLetter,
  usePatchLetterLine,
  usePriceLetter,
} from '../api/letterQueries'
import type { LetterPatch, LinePatch } from '../api/letterTypes'
import { FinalizeLetterModal } from '../components/FinalizeLetterModal'
import { LetterBodyEditor } from '../components/LetterBodyEditor'
import { LetterHeaderForm } from '../components/LetterHeaderForm'
import { LetterLinesGrid } from '../components/LetterLinesGrid'
import { splitLines } from '../utils/letter-lines'

/** Work view for one price change letter: details, body, products, then finalize. */
export function PriceChangeLetterPage() {
  const { letterId } = useParams({ strict: false }) as { letterId?: string }
  const navigate = useNavigate()
  const { data, isLoading, isError } = usePriceLetter(letterId ?? null)
  const patchLetter = usePatchLetter()
  const patchLine = usePatchLetterLine()
  const finalize = useFinalizeLetter()
  const attach = useAttachLetter()
  const del = useDeleteLetter()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const id = letterId ?? ''
  const { mutate: patchLetterMutate } = patchLetter
  const { mutate: patchLineMutate } = patchLine

  const onPatchLetter = useCallback(
    (changes: LetterPatch) =>
      patchLetterMutate({ letterId: id, changes }, { onError: (e) => toast.error((e as Error).message) }),
    [id, patchLetterMutate],
  )
  const onSaveBody = useCallback((html: string) => onPatchLetter({ body_html: html }), [onPatchLetter])
  const onPatchLine = useCallback(
    (lineId: number, changes: LinePatch) =>
      patchLineMutate({ letterId: id, lineId, changes }, { onError: (e) => toast.error((e as Error).message) }),
    [id, patchLineMutate],
  )

  const split = useMemo(() => splitLines(data?.lines ?? []), [data])

  if (isLoading) return <div className="p-6 text-sm text-secondary">Loading…</div>
  if (isError || !data) {
    return <EmptyState variant="error" title="Letter not found" description="It may have been deleted." />
  }
  const { letter, lines } = data
  const locked = letter.status === 'finalized'

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-canvas">
      <PageHeaderBand title={`Price change letter — ${letter.partner_name}`}>
        <Chip variant={locked ? 'success' : 'neutral'}>{locked ? 'Finalized' : 'Draft'}</Chip>
        <span className="text-role-metadata">
          {split.priced.length} priced · {split.discontinued.length} no longer supplied · {split.excluded.length} left off
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigate({ to: '/pricelists/letters' } as never)}
        >
          <ArrowLeft size={14} /> All letters
        </Button>
        <a
          href={letterPdfUrl(letter.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-accent underline"
        >
          <Eye size={14} /> Preview PDF
        </a>
        {locked ? (
          <>
            <a href={letterPdfUrl(letter.id, true)} className="inline-flex items-center gap-1 text-sm text-accent underline">
              <Download size={14} /> Download PDF
            </a>
            {letter.odoo_attachment_id ? (
              <Chip variant="success" size="xs">
                <CheckCircle2 size={12} /> Attached to Odoo
              </Chip>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => attach.mutate(letter.id)} disabled={attach.isPending}>
                {attach.isPending ? 'Retrying…' : 'Retry attach to Odoo'}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={del.isPending}
              onClick={() => {
                if (!window.confirm(`Delete this draft letter for ${letter.partner_name}? This cannot be undone.`)) return
                del.mutate(letter.id, {
                  onSuccess: () => {
                    toast.success('Draft deleted')
                    void navigate({ to: '/pricelists/letters' } as never)
                  },
                  onError: (e) => toast.error((e as Error).message),
                })
              }}
            >
              <Trash2 size={14} /> {del.isPending ? 'Deleting…' : 'Delete draft'}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setConfirmOpen(true)}>
              <FileCheck2 size={14} /> Finalize…
            </Button>
          </>
        )}
      </PageHeaderBand>

      {locked && letter.attach_error && !letter.odoo_attachment_id ? (
        <div className="border-b border-danger bg-danger-bg px-6 py-2 text-sm text-danger-text">
          The letter is final, but attaching it to Odoo failed: {letter.attach_error}
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4">
        <LetterHeaderForm letter={letter} onPatch={onPatchLetter} />
        <LetterBodyEditor letter={letter} onSave={onSaveBody} />
        <LetterLinesGrid
          lines={lines}
          symbol={letter.currency_symbol}
          showTariff={letter.show_tariff}
          locked={locked}
          onPatch={onPatchLine}
        />
      </div>

      <FinalizeLetterModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        customer={letter.partner_name}
        pending={finalize.isPending}
        error={finalize.isError ? (finalize.error as Error).message : null}
        onConfirm={() =>
          finalize.mutate(letter.id, {
            onSuccess: (d) => {
              setConfirmOpen(false)
              toast.success(
                d.letter.attach_error ? 'Finalized. Attaching to Odoo failed, retry from the header.' : 'Letter finalized',
              )
            },
          })
        }
      />
    </div>
  )
}
